import { useState, useRef, useEffect, useCallback } from 'react';
import { sendChatMessageStream } from '../../services/chat';
import { getConversationMessages, generateConversationSummary } from '../../services/conversations';
import { generateArtifactsFromConversation, ConversationArtifacts } from '../../services/artifacts';
import { previewCareerExperiencesFromConversation, ProposedCareerExperience } from '../../services/careerMemory';
import { useLanguage } from '../../contexts/LanguageContext';
import { VoiceMode } from './VoiceMode';
import { saveVoiceTranscript } from '../../services/voiceConversation';
import type { TranscriptEntry } from '../../hooks/useGeminiLive';

const WEEKLY_PENDING_QUEUE_STORAGE_KEY = 'careerMemory.pendingQueue';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  createdAt?: string | null;
}

interface WeeklyCheckInProps {
  conversationId?: string | null;
  onEndCheckIn?: (options?: { redirectToTimeline?: boolean }) => void;
}

export function WeeklyCheckIn({ conversationId: initialConversationId, onEndCheckIn }: WeeklyCheckInProps = {}) {
  const { t, language } = useLanguage();
  const tRef = useRef(t);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(initialConversationId || undefined);
  const [error, setError] = useState<string>('');
  const [streamingContent, setStreamingContent] = useState<string>('');

  const [showArtifacts, setShowArtifacts] = useState(false);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [artifactsError, setArtifactsError] = useState<string>('');
  const [artifacts, setArtifacts] = useState<ConversationArtifacts | null>(null);

  const [voiceModeActive, setVoiceModeActive] = useState(false);
  const [speakingMessageIdx, setSpeakingMessageIdx] = useState<number | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(!!initialConversationId);
  const [endingSession, setEndingSession] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const formatMessageDate = (dateString?: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    // If within last hour, show "X minutes ago"
    if (diffMins < 60) {
      return diffMins === 0 ? t('common.justNow') : `${diffMins} ${t('common.minAgo')}`;
    }

    // If today, show time
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // If this year, show date without year
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // Otherwise show full date
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  // Load existing conversation or show initial greeting
  useEffect(() => {
    const loadConversation = async () => {
      if (initialConversationId) {
        setLoadingConversation(true);
        try {
          const data = await getConversationMessages(initialConversationId);
          setMessages(data.messages.map(msg => ({
            role: msg.role,
            content: msg.content,
            model: msg.model,
            usage: msg.usage,
            createdAt: msg.createdAt,
          })));
        } catch (err: any) {
          console.error('Failed to load conversation:', err);
          setError(tRef.current('weeklyCheckin.errors.loadConversation'));
        } finally {
          setLoadingConversation(false);
        }
      } else {
        // Initial greeting for new conversations
        setMessages([
          {
            role: 'assistant',
            content: tRef.current('weeklyCheckin.greeting'),
            createdAt: new Date().toISOString(),
          },
        ]);
      }
    };

    loadConversation();
  }, [initialConversationId]);

  const handleGenerateArtifacts = async () => {
    if (!conversationId || artifactsLoading) return;

    setArtifactsLoading(true);
    setArtifactsError('');
    setArtifacts(null);
    setShowArtifacts(true);

    try {
      const data = await generateArtifactsFromConversation(conversationId, { language });
      setArtifacts(data);
    } catch (err: any) {
      console.error('Failed to generate artifacts:', err);
      setArtifactsError(err.message || t('weeklyCheckin.errors.generateArtifacts'));
    } finally {
      setArtifactsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setError('');

    // Add user message to UI
    setMessages((prev) => [...prev, { role: 'user', content: userMessage, createdAt: new Date().toISOString() }]);

    setLoading(true);
    setStreamingContent('');
    let accumulatedContent = '';

    try {
      // Send to backend with streaming
      await sendChatMessageStream(userMessage, conversationId, {
        onConversationId: (id) => {
          if (!conversationId) {
            setConversationId(id);
          }
        },
        onChunk: (content) => {
          console.log('📝 Chunk received:', { chunkLength: content.length, chunkPreview: content.substring(0, 50) });
          accumulatedContent += content;
          setStreamingContent(accumulatedContent);
        },
        onDone: (data) => {
          // Add complete assistant response to messages
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: accumulatedContent,
              model: data.model,
              usage: data.usage,
              createdAt: new Date().toISOString(),
            },
          ]);
          setStreamingContent('');
          setLoading(false);
        },
        onError: (errorMsg) => {
          setError(errorMsg);
          setStreamingContent('');
          setLoading(false);
        },
      }, { language });
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(
        err.message ||
        t('weeklyCheckin.errors.sendMessageDefault')
      );
      setStreamingContent('');
      setLoading(false);
    }
  };

  const handleVoiceClose = useCallback(async (transcript: TranscriptEntry[]) => {
    setVoiceModeActive(false);
    if (transcript.length > 0) {
      // Save transcript to Firestore
      try {
        const savedId = await saveVoiceTranscript(conversationId, transcript);
        if (!conversationId) {
          setConversationId(savedId);
        }
        // Generate summary in background (fire-and-forget)
        const targetId = conversationId || savedId;
        if (targetId) {
          generateConversationSummary(targetId).catch(err => {
            console.warn('Voice summary generation failed (non-blocking):', err);
          });
        }
        // Append to local messages for display
        const newMessages = transcript.map(entry => ({
          role: entry.role as 'user' | 'assistant',
          content: entry.text,
          createdAt: entry.timestamp,
        }));
        setMessages(prev => [...prev, ...newMessages]);
      } catch (err: any) {
        console.error('Failed to save voice transcript:', err);
        setError(err.message || t('weeklyCheckin.failedSaveVoice'));
      }
    }
  }, [conversationId]);

  const handleSpeak = useCallback((text: string, idx: number) => {
    if (speakingMessageIdx === idx) {
      window.speechSynthesis.cancel();
      setSpeakingMessageIdx(null);
      return;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === 'fr' ? 'fr-FR' : 'en-US';
    utterance.rate = 1.0;
    utterance.onend = () => setSpeakingMessageIdx(null);
    utterance.onerror = () => setSpeakingMessageIdx(null);
    setSpeakingMessageIdx(idx);
    window.speechSynthesis.speak(utterance);
  }, [speakingMessageIdx, language]);

  const handleEndSession = useCallback(async () => {
    if (endingSession) {
      return;
    }

    setEndingSession(true);

    // Kick off summary generation immediately so it's ready when the list loads.
    // Fire-and-forget — don't block the UX or the experience extraction below.
    if (conversationId && messages.length > 0) {
      generateConversationSummary(conversationId).catch(err => {
        console.warn('Summary generation on session end failed (non-blocking):', err);
      });
    }

    try {
      if (conversationId && messages.length > 2) {
        try {
          const preview = await previewCareerExperiencesFromConversation(conversationId);
          const extracted = Array.isArray(preview.extractedExperiences)
            ? preview.extractedExperiences
            : [];

          if (extracted.length > 0) {
            const pendingItems = extracted.map((item: ProposedCareerExperience, index: number) => ({
              ...item,
              pendingId: `${Date.now()}-${index}`,
            }));

            try {
              sessionStorage.setItem(WEEKLY_PENDING_QUEUE_STORAGE_KEY, JSON.stringify(pendingItems));
            } catch (storageErr) {
              console.warn('Failed to store pending queue in sessionStorage:', storageErr);
            }

            onEndCheckIn?.({ redirectToTimeline: true });
            return;
          }
        } catch (extractErr) {
          console.warn('Weekly check-in auto extraction failed (non-blocking):', extractErr);
        }
      }
      onEndCheckIn?.();
    } finally {
      setEndingSession(false);
    }
  }, [endingSession, conversationId, messages.length, onEndCheckIn]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  return (
    <div data-testid="weekly-checkin" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: 'var(--surface)',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-sm)',
    }}>
      {voiceModeActive && (
        <VoiceMode onClose={handleVoiceClose} chatHistory={messages.map(m => ({ role: m.role, content: m.content }))} />
      )}

      {showArtifacts && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            zIndex: 1000,
          }}
          onClick={() => {
            setShowArtifacts(false);
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '720px',
              backgroundColor: 'var(--surface)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-sm)',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '16px' }}>{t('weeklyCheckin.artifactsModal.title')}</h3>
                <p style={{ margin: '6px 0 0 0', color: 'var(--text-muted)', fontSize: '12px' }}>
                  {t('weeklyCheckin.artifactsModal.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setShowArtifacts(false)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                {t('common.close')}
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {artifactsLoading && (
                <p style={{ margin: 0, color: 'var(--text-muted)' }}>{t('weeklyCheckin.artifactsModal.generating')}</p>
              )}

              {artifactsError && (
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: 'var(--danger-soft)',
                    color: 'var(--danger)',
                    fontSize: '14px',
                  }}
                >
                  {artifactsError}
                </div>
              )}

              {artifacts && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <h4 style={{ margin: '0 0 10px 0' }}>{t('weeklyCheckin.artifactsModal.resumeBullets')}</h4>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      {artifacts.resumeBullets.map((b, i) => (
                        <li key={i} style={{ marginBottom: '8px', color: 'var(--text)' }}>
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 style={{ margin: '0 0 10px 0' }}>{t('weeklyCheckin.artifactsModal.linkedinDraft')}</h4>
                    <div
                      style={{
                        padding: '12px 14px',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        backgroundColor: 'var(--surface-muted)',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {artifacts.linkedinPost}
                    </div>
                  </div>

                  {artifacts.model && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {t('weeklyCheckin.artifactsModal.model')}: {artifacts.model}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid var(--border)',
      }}>
        <h2 style={{ margin: 0, fontSize: '24px' }}>{t('weeklyCheckin.title')}</h2>
        <p style={{ margin: '8px 0 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
          {t('weeklyCheckin.subtitle')}
        </p>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
        {loadingConversation && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            gap: '20px',
          }}>
            <div style={{
              border: '4px solid var(--border)',
              borderTop: '4px solid var(--primary)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
              {t('weeklyCheckin.loadingConversation')}
            </p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {!loadingConversation && messages.map((msg, idx) => (
          <div
            key={idx}
            data-testid="chat-message"
            data-role={msg.role}
            style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '70%',
            }}
          >
            {msg.createdAt && (
              <div style={{
                fontSize: '11px',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                textAlign: msg.role === 'user' ? 'right' : 'left',
              }}>
                {formatMessageDate(msg.createdAt)}
              </div>
            )}
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: msg.role === 'user' ? 'var(--primary)' : 'var(--surface-muted)',
                color: msg.role === 'user' ? 'white' : 'var(--text)',
              }}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
              {msg.model && (
                <div style={{
                  marginTop: '8px',
                  fontSize: '11px',
                  opacity: 0.7,
                }}>
                  {msg.model}
                </div>
              )}
              {msg.role === 'assistant' && (
                <button
                  data-testid="tts-button"
                  type="button"
                  onClick={() => handleSpeak(msg.content, idx)}
                  title={speakingMessageIdx === idx ? t('voice.stopSpeaking') : t('voice.listenToMessage')}
                  style={{
                    marginTop: '6px',
                    padding: '4px 8px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    opacity: 0.6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {speakingMessageIdx === idx ? '\u23F9' : '\uD83D\uDD0A'}
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {speakingMessageIdx === idx ? t('voice.stopSpeaking') : t('voice.listen')}
                  </span>
                </button>
              )}
            </div>
          </div>
        ))}

        {!loadingConversation && streamingContent && (
          <div
            style={{
              alignSelf: 'flex-start',
              maxWidth: '70%',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: 'var(--surface-muted)',
              }}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>{streamingContent}</div>
            </div>
          </div>
        )}

        {loading && !streamingContent && (
          <div
            style={{
              alignSelf: 'flex-start',
              maxWidth: '70%',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: 'var(--surface-muted)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div>{t('weeklyCheckin.thinking')}</div>
                <div style={{
                  display: 'inline-flex',
                  gap: '4px',
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--primary)',
                    animation: 'pulse 1.4s infinite ease-in-out',
                    animationDelay: '0s',
                  }} />
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--primary)',
                    animation: 'pulse 1.4s infinite ease-in-out',
                    animationDelay: '0.2s',
                  }} />
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--primary)',
                    animation: 'pulse 1.4s infinite ease-in-out',
                    animationDelay: '0.4s',
                  }} />
                </div>
              </div>
              <style>{`
                @keyframes pulse {
                  0%, 80%, 100% {
                    opacity: 0.3;
                    transform: scale(0.8);
                  }
                  40% {
                    opacity: 1;
                    transform: scale(1);
                  }
                }
              `}</style>
            </div>
          </div>
        )}

        {error && (
          <div
            data-testid="weekly-checkin-error"
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              backgroundColor: 'var(--danger-soft)',
              color: 'var(--danger)',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '20px',
        borderTop: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <button
            data-testid="weekly-checkin-generate-artifacts"
            onClick={handleGenerateArtifacts}
            disabled={!conversationId || loading || messages.length < 2}
            style={{
              padding: '10px 14px',
              backgroundColor: !conversationId || loading || messages.length < 2 ? 'var(--surface-muted)' : 'var(--surface)',
              color: !conversationId || loading || messages.length < 2 ? 'var(--text-muted)' : 'var(--primary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              cursor: !conversationId || loading || messages.length < 2 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '600',
            }}
            title={!conversationId ? t('weeklyCheckin.tooltips.sendFirst') : undefined}
          >
            {t('weeklyCheckin.generateArtifacts')}
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '12px',
          }}
        >
          <textarea
            data-testid="weekly-checkin-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('weeklyCheckin.placeholder')}
            disabled={loading}
            rows={2}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              fontSize: '14px',
              outline: 'none',
              resize: 'vertical',
            }}
          />
          <button
            data-testid="weekly-checkin-voice"
            type="button"
            onClick={() => setVoiceModeActive(true)}
            disabled={loading}
            title={t('voice.startVoiceMode')}
            style={{
              padding: '12px',
              backgroundColor: 'var(--surface)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              fontSize: '18px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '48px',
            }}
          >
            {'\uD83C\uDFA4'}
          </button>
          <button
            data-testid="weekly-checkin-send"
            type="submit"
            disabled={loading || !input.trim()}
            style={{
              padding: '12px 24px',
              backgroundColor: loading || !input.trim() ? 'var(--border)' : 'var(--primary)',
              color: loading || !input.trim() ? 'var(--text-muted)' : 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {t('chat.send')}
          </button>
        </form>

        {/* End Check-In button */}
        {messages.length > 2 && onEndCheckIn && (
          <button
            data-testid="weekly-checkin-end"
            onClick={handleEndSession}
            disabled={endingSession}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: endingSession ? 'var(--border)' : 'var(--text-muted)',
              color: endingSession ? 'var(--text-muted)' : 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: endingSession ? 'not-allowed' : 'pointer',
            }}
          >
            {endingSession ? t('common.loading') : t('weeklyCheckin.endAndReturn')}
          </button>
        )}
      </div>
    </div>
  );
}
